<template>
  <BittsLoading :is-loading="isLoading">
    <div class="c-my-data-sharing-tab">
      <div class="flex flex-col gap-16">
        <div class="flex flex-col gap-4">
          <h2 class="font-bold text-lg">My Populations</h2>
          <h3 class="text-neutral-text">
            Manage how your populations are shared with this partner.
          </h3>
        </div>
        <PopulationActions
          ref="populationActionsRef"
          :event-site="EVENT_SITES.MY_DATA_SHARING_TAB"
          :is-action-bar-disabled="isLoading || !allPopulations.length"
          :show-edit-button="false"
          :include-overrides-filter="
            hasSharingOverrides && hasSharingPermission
          "
          :initial-overrides-filter="initialOverridesFilter"
          :initial-segment="initialSegment"
          @search-applied="(value) => applySearch(value)"
          @filters-applied="(filterValues) => applyFilters(filterValues)"
        />
      </div>
      <!-- Empty state when no populations exist -->
      <BittsEmptyState
        v-if="!allPopulations.length"
        title="No populations available"
        description="Create populations to start sharing data with this partner."
        :font-awesome-icon="['fak', 'population']"
        class="mt-16"
      />
      <!-- Empty state when filtered results are empty -->
      <BittsEmptyState
        v-else-if="!hasFilteredResults"
        title="No Results"
        description="Try different filters or search term to find your population"
        :button="{
          text: 'Clear Filters',
          size: 'small',
          variant: 'outline',
          type: 'primary',
        }"
        font-awesome-icon-class="c-my-data-sharing-tab__empty-state-icon"
        :font-awesome-icon="['fak', 'search']"
        :font-awesome-icon-style="{ height: '52px', width: '52px' }"
        @button-clicked="handleClearFilters"
      />
      <div v-else class="flex flex-col gap-8 pb-16">
        <ListContainer
          :list="populationsWithSubheaders"
          :has-divider="() => false"
          :get-key="getKey"
          :show-hover="true"
        >
          <template #header>
            <div class="c-my-data-sharing-tab__header">
              <template v-for="field in headerFields" :key="field.label">
                <div v-if="field.tooltip" class="flex items-center gap-4 px-12">
                  <p class="text-sm font-bold text-neutral-text">
                    {{ field.label }}
                  </p>
                  <BittsTooltip placement="top">
                    <FontAwesomeIcon
                      :icon="['fak', 'information-circle']"
                      :style="{
                        height: '12px',
                        width: '12px',
                        color: 'currentColor',
                      }"
                      class="text-neutral-accent flex justify-center items-center"
                    />
                    <template #title>
                      {{ field.tooltip }}
                    </template>
                  </BittsTooltip>
                </div>
                <div v-else class="flex items-center gap-4 px-12">
                  <p class="text-sm font-bold text-neutral-text">
                    {{ field.label }}
                  </p>
                </div>
              </template>
            </div>
          </template>
          <template #default="{ item }">
            <!-- Subheader for Custom/Default sections -->
            <template v-if="isSubheader(item)">
              <div class="c-my-data-sharing-tab__subheader">
                <span class="text-sm font-bold text-neutral-text-strong">
                  {{ item.title }}
                </span>
              </div>
            </template>
            <!-- Population row -->
            <template v-else>
              <MyDataSharingRow
                :population="item.population"
                :partner-id="partnerId"
                :data-share="getDataShareForPopulation(item.population.id)"
                :create-data-share="createCustomDataShare"
                :update-data-share="updateDataShare"
                :preset-options="getPresetOptionsForPopulation(item.population)"
                :single-share-presets="singleSharePresets"
                :reset-data-share="resetDataShare"
                :delete-override="deleteOverride"
                :current-population-to-population="
                  currentPopulationToPopulation
                "
                :has-changes="hasPopulationChanges(item.population.id)"
                :has-unsaved-changes="hasChanges"
                :is-custom="item.isCustom"
                @show-pop-overrides-modal="
                  onShowPopOverridesModalClicked(item.population.id)
                "
              />
            </template>
          </template>
          <template #extra="{ item }">
            <template v-if="!isSubheader(item)">
              <PopulationToPopulationSettings
                :population-id="item.population.id"
                :partner-name="partnerName"
                :partner-id="partnerId"
                :delete-override="deleteOverride"
                :current-population-to-population="
                  currentPopulationToPopulation
                "
                :has-changes="hasPopulationChanges(item.population.id)"
              />
            </template>
          </template>
        </ListContainer>
      </div>
      <PopOverridesModal
        v-if="showPopOverridesModal && hasSharingOverrides"
        :population-id="overridePopulationId"
        @closed="onClosePopOverridesModal"
      />
    </div>
  </BittsLoading>
</template>

<script setup lang="ts">
import { BittsEmptyState, BittsLoading, BittsTooltip } from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, ref } from 'vue';

import ListContainer from '@/components/ListContainer.vue';
import PopOverridesModal from '@/components/partners/DataSharingSettings/SettingsModal/SharingSettingsTab/components/PopOverridesModal.vue';
import PopulationToPopulationSettings from '@/components/partners/DataSharingSettings/SettingsModal/SharingSettingsTab/components/PopulationToPopulationSettings.vue';
import PopulationActions from '@/components/sharing-settings/PopulationActions.vue';

import useAuth from '@/composables/useAuth';
import { SHARING_OVERRIDES_ENABLED } from '@/constants/feature_flags';
import { useFeatureFlagStore, usePartnersStore } from '@/stores';
import type { SegmentType } from '@/types/populations';

import useMyDataSharing, {
  type PopulationListItem,
  type SubheaderItem,
} from '../composables/useMyDataSharing';

import MyDataSharingRow from './MyDataSharingRow.vue';

type Props = {
  partnerId: number;
  initialOverridesFilter?: boolean;
  initialSegment?: SegmentType;
};

const props = withDefaults(defineProps<Props>(), {
  initialOverridesFilter: undefined,
  initialSegment: undefined,
});

const emit = defineEmits<(e: 'bulk-update', success: boolean) => void>();

const partnersStore = usePartnersStore();

const {
  isLoading,
  allPopulations,
  populationsWithSubheaders,
  singleSharePresets,
  hasChanges,
  currentPopulationToPopulation,
  getDataShareForPopulation,
  getPresetOptionsForPopulation,
  resetDataShare,
  createCustomDataShare,
  updateDataShare,
  deleteOverride,
  bulkUpdateDataShares,
  applySearch,
  applyFilters,
  clearFilters,
  hasPopulationChanges,
} = useMyDataSharing(props.partnerId);

const populationActionsRef = ref<InstanceType<typeof PopulationActions> | null>(
  null,
);

function handleClearFilters() {
  clearFilters();
  populationActionsRef.value?.clearAllFilters();
}

const hasFilteredResults = computed(
  () => populationsWithSubheaders.value.length > 0,
);

/**
 * Type guard to check if an item is a subheader
 */
function isSubheader(item: PopulationListItem): item is SubheaderItem {
  return 'type' in item && item.type === 'subheader';
}

/**
 * Get key for list items (handles both subheaders and populations)
 */
function getKey(item: PopulationListItem): string | number {
  if (isSubheader(item)) {
    return item.id;
  }
  return item.population.id;
}

const partnerName = computed(() => {
  return partnersStore.getPartnerOrgById(props.partnerId)?.name ?? '';
});

const headerFields = computed(() => {
  return [
    {
      label: 'Population',
    },
    {
      label: 'Sharing Level',
      tooltip:
        "Choose how much of this population's data you want to share with partners.",
    },
    {
      label: 'Field Presets',
      tooltip: 'Choose which data fields to share by default.',
    },
    {
      label: '',
    },
  ];
});

const ffStore = useFeatureFlagStore();
const hasSharingOverrides = computed(() =>
  ffStore.hasFeatureFlag(SHARING_OVERRIDES_ENABLED),
);

const { hasPermission } = useAuth();
const hasSharingPermission = computed(() => hasPermission('manage:sharing'));

const showPopOverridesModal = ref(false);
const overridePopulationId = ref<number | null>(null);

function onClosePopOverridesModal() {
  showPopOverridesModal.value = false;
}

function onShowPopOverridesModalClicked(populationId: number) {
  overridePopulationId.value = populationId;
  showPopOverridesModal.value = true;
}

async function triggerBulkUpdate() {
  try {
    await bulkUpdateDataShares();
    emit('bulk-update', true);
    return true;
  } catch (error) {
    emit('bulk-update', false);
    return false;
  }
}

defineExpose({
  triggerBulkUpdate,
  hasChanges,
});
</script>

<style lang="pcss" scoped>
@reference "../../../../../../css/theme.css";
.c-my-data-sharing-tab {
  @apply flex flex-col m-24 gap-16;
}

.c-my-data-sharing-tab__header {
  @apply grid gap-16 px-4 py-8 w-full items-center border-b border-neutral-border;
  grid-template-columns: 3fr 2fr 2fr 1fr;
}
.c-my-data-sharing-tab__subheader {
  @apply bg-neutral-background-weak px-16 py-8;
}
:deep(.c-my-data-sharing-tab__empty-state-icon) {
  @apply text-neutral-text-weak p-[26px] rounded-[52px];
  background: radial-gradient(
      136.96% 69.92% at 50.09% -12.42%,
      color-mix(in srgb, theme(colors.neutral-accent) 20%, transparent),
      color-mix(in srgb, theme(colors.neutral-accent) 0%, transparent)
    ),
    linear-gradient(0deg, theme(colors.white), theme(colors.white));
}
:deep(
    .c-list-container[data-show-hover='true']
      .item:hover
      + div
      .c-population-to-population-settings-card-hub
  ) {
  @apply bg-neutral-background-weak;
}
</style>
